import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle2, Shield, Cpu, FileText, Globe } from 'lucide-react';

const trustPoints = [
  {
    icon: CheckCircle2,
    label: 'Live on Base Sepolia',
    detail: 'Testnet deployment',
    color: '#00FFD0',
  },
  {
    icon: Shield,
    label: 'Vault Tested On-Chain',
    detail: 'Verified contracts',
    color: '#00D4FF',
  },
  {
    icon: Cpu,
    label: 'AI Risk Engine',
    detail: 'Autonomous allocation',
    color: '#0066FF',
  },
  {
    icon: FileText,
    label: 'Docs',
    detail: 'Full technical docs',
    color: '#627EEA',
    link: 'https://docs.raxprotocol.xyz/',
  },
  {
    icon: Globe,
    label: 'Ecosystem Partners',
    detail: 'Infrastructure support',
    color: '#F0B90B',
  },
];

export default function TrustLayer() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-50px' });

  return (
    <section ref={sectionRef} data-testid="trust-layer" className="relative py-8">
      {/* Top divider */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/5 h-px bg-gradient-to-r from-transparent via-rax-border/30 to-transparent" />
      
      <div className="mx-auto max-w-5xl px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 sm:gap-x-8"
          data-testid="trust-points"
        >
          {trustPoints.map((point, index) => {
            const Icon = point.icon;
            const content = (
              <motion.div
                key={point.label}
                initial={{ opacity: 0, y: 8 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.05 + index * 0.06, duration: 0.4 }}
                className="flex items-center gap-2 group"
                data-testid={`trust-point-${index}`}
              >
                <div 
                  className="flex h-7 w-7 items-center justify-center rounded-md transition-colors"
                  style={{ 
                    backgroundColor: `${point.color}10`,
                  }}
                >
                  <Icon 
                    size={14} 
                    strokeWidth={2}
                    style={{ color: point.color }}
                  />
                </div>
                <div className="flex flex-col">
                  <span className="font-body text-xs font-medium text-rax-heading leading-tight">
                    {point.label}
                  </span>
                  <span className="font-mono text-[9px] text-rax-body/50 uppercase tracking-wide">
                    {point.detail}
                  </span>
                </div>
              </motion.div>
            );

            if (point.link) {
              return (
                <a
                  key={point.label}
                  href={point.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  {content}
                </a>
              );
            }
            return content;
          })}
        </motion.div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/5 h-px bg-gradient-to-r from-transparent via-rax-border/30 to-transparent" />
    </section>
  );
}
